/*
 * Original Source Analysis (CXO) — leads and paid users per original source.
 * Same grouped bar chart (Leads blue, Paid Users red) and the same table with a Total row.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { Bar } from "react-chartjs-2";
import { getOriginalSourceAnalysis } from "../../Action/questionBankAction";
import { getExamTypeSectionList } from "../../Action/adminPanelAction";
import { CustomAdminTable, CustomPageLayout } from "../../Component/customComponents";
import { TableTitle, formatDateRange, todayISO } from "../Common/transactionCommon";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const n = (v) => Number(v) || 0;
const fmt = (v) => n(v).toLocaleString("en-IN");

const OriginalSourceAnalysis = () => {
  const dispatch = useDispatch();
  const [sections, setSections] = useState([]);
  const [filters, setFilters] = useState(() => ({ startDate: todayISO(), endDate: todayISO(), section: "", subSection: "" }));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  /** same request as before: { startDate, endDate, examSectionId?, examSubSectionId? } */
  const fetchData = (f) => {
    setLoading(true);
    dispatch(
      getOriginalSourceAnalysis(
        { startDate: f.startDate, endDate: f.endDate, ...(f.section && { examSectionId: f.section._id }), ...(f.subSection && { examSubSectionId: f.subSection._id }) },
        (res) => {
          setReport(Array.isArray(res) ? res[0] || null : null);
          setLoading(false);
        },
        () => {
          setReport(null);
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    dispatch(getExamTypeSectionList((res) => setSections(res || [])));
    fetchData(filters); // today on open, as before
  }, []);

  const origins = report?.origins || [];
  const label = (o) => o?.origin || "Unknown";

  // same chart as before
  const maxValue = Math.max(0, ...origins.map((o) => n(o?.leads)), ...origins.map((o) => n(o?.paidUsers)));
  const chartData = {
    labels: origins.map(label),
    datasets: [
      { label: "Leads", data: origins.map((o) => o?.leads), backgroundColor: "#4F81BC" },
      { label: "Paid Users", data: origins.map((o) => o?.paidUsers), backgroundColor: "#C0504E" },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true, max: maxValue, position: "left" },
      y1: { beginAtZero: true, max: maxValue, position: "right", display: false, grid: { drawOnChartArea: false } },
    },
  };

  const tableRows = useMemo(
    () =>
      origins.length
        ? [
            ...origins,
            { _total: true, origin: "Total", leads: report?.totalLeadCount, contribution: report?.totalContributions, paidUsers: report?.totalPaidUsers, paidContribution: report?.totalPaidContribution },
          ]
        : [],
    [report],
  );

  const bold = (r, v) => (r._total ? <strong>{v}</strong> : v);
  const columns = [
    { key: "origin", header: "Source Type", minWidth: 160, render: (v, r) => <strong>{r._total ? "Total" : v || "Unknown"}</strong> },
    { key: "leads", header: "Lead Count", align: "right", render: (v, r) => bold(r, fmt(v)) },
    { key: "contribution", header: "Contribution (%)", align: "right", render: (v, r) => bold(r, `${v || 0}%`) },
    { key: "paidUsers", header: "Paid Users", align: "right", render: (v, r) => bold(r, fmt(v)) },
    { key: "paidContribution", header: "Paid Conversion", align: "right", render: (v, r) => bold(r, `${v || 0}%`) },
  ];

  const filterFields = useMemo(
    () => [
      { key: "startDate", label: "Start Date", type: "date", half: true, isRequired: true, defaultValue: todayISO },
      { key: "endDate", label: "End Date", type: "date", half: true, isRequired: true, defaultValue: todayISO, minDate: (v) => v.startDate, validate: (value, v) => (value && v.startDate && value < v.startDate ? "End date can't be before the start date" : "") },
      { key: "section", label: "Section Name", type: "dropdown", options: sections, clears: ["subSection"] },
      { key: "subSection", label: "Sub Section", type: "dropdown", options: (v) => v.section?.subSection || [], disabled: (v) => !v.section, hint: (v) => (!v.section ? "Select a section first" : undefined) },
    ],
    [sections],
  );

  return (
    <CustomPageLayout title="ORIGINAL SOURCE ANALYSIS">
      <div className="d-flex flex-column gap-3">
        {origins.length > 0 && (
          <section className="bg-white border rounded-3 shadow-sm p-3">
            <div style={{ height: 400 }}>
              <Bar data={chartData} options={options} />
            </div>
          </section>
        )}
        <CustomAdminTable
          id="original-source-analysis"
          title={<TableTitle title={`Sources (${origins.length})`} from={filters.startDate} to={filters.endDate} />}
          columns={columns}
          data={tableRows}
          loading={loading}
          rowKey={(r, i) => (r._total ? "total" : `${label(r)}-${i}`)}
          rowClassName={(r) => (r._total ? "cat-rowGrandTotal" : "")}
          advancedFilters={filterFields}
          advancedFilterValues={filters}
          onApplyFilters={(values) => {
            setFilters(values);
            fetchData(values);
          }}
          applyFiltersOnReset
          filterSubtitle="Pick the dates, optionally a section"
          emptyTitle="No records"
          emptyDescription={`Nothing for ${formatDateRange(filters.startDate, filters.endDate)}.`}
        />
      </div>
    </CustomPageLayout>
  );
};

export default OriginalSourceAnalysis;
